import { Box, FormControl, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material"
import { HelpOutlined } from "@mui-symbols-material/w300"
import type { ReactElement } from "react"
import React, { useMemo } from "react"
import { Controller, useFormContext } from "react-hook-form"

import { PAY_INVOICE_LABELS, PAY_PATTERN_LABELS } from "../constants"
import type { PaymentTermsSchemaFormValues } from "../schemas/PaymentTermsSchema"
import { getPayRunSchedulePayDayTooltipMessage } from "../utils/paymentTerms"

import { AerosDatePicker } from "@/components/UiKit"
import {
  FIELD_ERROR_CLASSES,
  FIELD_LABEL_CLASSES,
  SELECT_STYLING_PROPS,
  TEXT_FIELD_STYLING_PROPS,
} from "@/constants/forms"
import { MaturityDateTypeGql, PayPatternGql } from "@/graphql/codegen/graphql"

const AddPaymentRuleFields = (): ReactElement => {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<PaymentTermsSchemaFormValues>()

  const payRunSchedulePayPatternValue = watch("payRunSchedule.payPattern") as PayPatternGql

  const payDaysTooltipMessage = useMemo(
    () => getPayRunSchedulePayDayTooltipMessage(payRunSchedulePayPatternValue),
    [payRunSchedulePayPatternValue]
  )

  return (
    <div>
      <Box className='flex items-end gap-3'>
        <Controller
          name='payRunSchedule.payPattern'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <FormControl className='min-w-[176px]'>
              <label id='pay-pattern-label' className={FIELD_LABEL_CLASSES}>
                Pay Pattern<span className='text-red-600'>*</span>
              </label>
              <Select
                {...field}
                {...SELECT_STYLING_PROPS}
                data-testid='pay-pattern-select'
                labelId='pay-pattern-label'
                error={!!error}
                required
              >
                {Object.values(PayPatternGql).map((value) => (
                  <MenuItem key={value} value={value}>
                    {PAY_PATTERN_LABELS[value]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />

        <Controller
          name='payRunSchedule.startDate'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Box className='relative'>
              <label className={FIELD_LABEL_CLASSES}>
                Start (reference date)<span className='text-red-600'>*</span>
              </label>
              <AerosDatePicker
                {...field}
                value={field.value ?? null}
                onChange={field.onChange}
                error={!!error}
                required
                data-testid='start-date-picker'
              />
            </Box>
          )}
        />
        <Controller
          name='payRunSchedule.payDays'
          control={control}
          render={({ field }) => (
            <Box className='relative'>
              <label className={FIELD_LABEL_CLASSES}>
                Pay Days<span className='text-red-600'>*</span>
              </label>
              <TextField
                {...field}
                {...TEXT_FIELD_STYLING_PROPS}
                name={field.name}
                placeholder='-'
                value={field.value ?? ""}
                slotProps={{
                  input: {
                    endAdornment: (
                      <Tooltip
                        title={
                          <Typography variant='body2' sx={{ whiteSpace: "pre-line" }}>
                            {payDaysTooltipMessage}
                          </Typography>
                        }
                        placement='top'
                      >
                        <HelpOutlined />
                      </Tooltip>
                    ),
                  },
                  htmlInput: {
                    className: "hide-stepper",
                  },
                }}
              />
            </Box>
          )}
        />
        <Controller
          name='payRunSchedule.invoiceMaturity.intervalBetweenPayRuns'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <FormControl className='min-w-[176px]'>
              <label id='interval-between-pay-runs-label' className={FIELD_LABEL_CLASSES}>
                Pay Invoices with Maturity Date Within<span className='text-red-600'>*</span>
              </label>
              <Select
                {...field}
                {...SELECT_STYLING_PROPS}
                data-testid='interval-between-pay-runs-select'
                labelId='interval-between-pay-runs-label'
                error={!!error}
                required
              >
                {Object.values(MaturityDateTypeGql).map((value) => (
                  <MenuItem key={value} value={value}>
                    {PAY_INVOICE_LABELS[value]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
        <Typography variant='body2' className='mb-3 content-center text-gray-700'>
          offset
        </Typography>
        <Controller
          name='payRunSchedule.invoiceMaturity.offsetInDays'
          control={control}
          render={({ field }) => (
            <Box className='relative'>
              <TextField
                {...field}
                {...TEXT_FIELD_STYLING_PROPS}
                name={field.name}
                placeholder='-'
                value={field.value ?? ""}
                slotProps={{
                  htmlInput: {
                    className: "hide-stepper",
                  },
                }}
              />
            </Box>
          )}
        />
      </Box>
      <div className={FIELD_ERROR_CLASSES}>
        <div>{errors?.payRunSchedule?.payPattern?.message}</div>
        <div>{errors?.payRunSchedule?.startDate?.message}</div>
        <div>{errors?.payRunSchedule?.payDays?.message}</div>
        <div>{errors?.payRunSchedule?.invoiceMaturity?.intervalBetweenPayRuns?.message}</div>
        <div>{errors?.payRunSchedule?.invoiceMaturity?.offsetInDays?.message}</div>
      </div>
    </div>
  )
}

export default AddPaymentRuleFields
